// AddContent.js

import React, { useRef, useState, useEffect } from 'react';
import {
  TextField, Divider, Typography, Box, Button, useMediaQuery, Grid,
  Accordion, AccordionSummary, AccordionDetails, styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import Descriptions from '../../components/details/Descriptions';
import Categories from '../../components/categories/Categories';
import KeywordInput from './KeywordInput';
import Rights from '../../components/details/Rights';
import { Literals } from '../../components/constants/Literals.js';
import Credits from './Credits.js';
import { useCountries } from '../../components/details/useCountries.js';
import CustomCheckbox from './CustomCheckbox';
import { convertDatationText, handleUpload } from './AddContentController.js';
import LoadingSpinner from './LoadingSpinner.js';

// eslint-disable-next-line no-unused-vars
import ChunkedUpload from '../upload/ChunkedUpload';


export function AddContent({ selectedContent }) {

  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);

  const [contentId, setContentId] = useState(''); // Track if we're editing an existing content

  const [triggerRender, setTriggerRender] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);

  // 1.1. Title and Author useRef
  const titleRef = useRef('');
  const artworkAuthorRef = useRef('');
  const [author, setAuthor] = useState('');
  const languageRef = useRef('');

  // 1.2 Descriptions, separate states + useRef for handling 
  const [descriptions, setDescriptions] = useState([]);
  const [currentDescription, setCurrentDescription] = useState('');
  const [currentDescriptionLang, setCurrentDescriptionLang] = useState('');
  const [currentDescriptionName, setCurrentDescriptionName] = useState('');

  const datationTextRef = useRef('');
  const datationHiddenRef = useRef(false);
  const datationKindRef = useRef(false);

  // 2. Classification 
  const categoriesRef = useRef([]);
  const [categories, setCategories] = useState([]);  // Manage the UI updates

  const keywordsRef = useRef([]); // Using useRef to store keywords

  // 3. Media

  const [hdPath, setHdPath] = useState('');
  const [sdPath, setSdPath] = useState('');
  const [thumbnailPath, setThumbnailPath] = useState('');
  

  const [selectedFiles, setSelectedFiles] = useState({
    hdFile: null,        // HD file
    sdFile: null,        // SD file
    thumbnailFile: null  // Thumbnail file
  });
  const mediaRef = useRef({
    hdPath: '',
    sdPath: '',
    thumbnailPath: ''
  });
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  // 4. Rights

  const [rights, setRights] = useState([]);
  const [currentRight, setCurrentRight] = useState('-11'); // Default to "No access"
  const [currentRightCountry, setCurrentRightCountry] = useState(Literals.ALL);

  // 5. Credits

  const countries = useCountries();

  const [credits, setCredits] = useState([]);
  const [creditName, setCreditName] = useState('');
  const [creditType, setCreditType] = useState('');
  const [creditLocation, setCreditLocation] = useState("ALL");  // Default to "ALL"

  // S. Saving

  const contentDataRef = useRef({
    title: '',
    artworkAuthor: '',
    datationText: '',
    datationStart: '',
    datationEnd: '',
    datationHidden: false,
    datationKind: false,
    categories: [],
    keywords: [], // array to hold keywords
    language: '',
    streaming: true,
    splittable: true,
    croppable: true,
    deconstructable: true,
    media: {
      hdPath: '',
      sdPath: '',
      thumbnailPath: '',
    },
  });

  useEffect(() => {
    if (selectedContent) {
      console.log("[AddContent] Full selectedContent object:", selectedContent);
  
      setContentId(selectedContent.id);
  
      // Set refs based on selectedContent using useRef
      titleRef.current = selectedContent.title || '';
      console.log("[AddContent] Title set to:", titleRef.current);
  
      artworkAuthorRef.current = selectedContent.author ? selectedContent.author.display_name : '';
      console.log("[AddContent] Artwork Author set to:", artworkAuthorRef.current);
  
      languageRef.current = selectedContent.language || '';
      console.log("[AddContent] Language set:", languageRef.current);

      setDescriptions(selectedContent.descriptions || []);
      console.log("[AddContent] Descriptions set to:", selectedContent.descriptions);

      const fallbackDescription = selectedContent.best_description || '';
      console.log("[AddContent] Fallback description set to:", fallbackDescription);
  
      datationTextRef.current = selectedContent.datationText || '';
      datationHiddenRef.current = selectedContent.datationHidden || false;
      datationKindRef.current = selectedContent.datationKind || false;
      console.log("[AddContent] Datation set: Text:", datationTextRef.current, "Hidden:", datationHiddenRef.current, "Kind:", datationKindRef.current);
  
      categoriesRef.current = selectedContent.categories || [];
      contentDataRef.current.categories = categoriesRef.current;
      console.log("[AddContent] Categories set:", categoriesRef.current);
  
      // Ensure keywordsRef is an array, even if keywords are provided as a string
      keywordsRef.current = Array.isArray(selectedContent.keywords) 
        ? selectedContent.keywords 
        : selectedContent.keywords ? selectedContent.keywords.split(',') : [];
      console.log("[AddContent] Keywords set:", keywordsRef.current);
  
      // Set media paths using existing structure
      const hd = selectedContent.urls?.find(url => url.kind === 'HD')?.url || '';
      const sd = selectedContent.urls?.find(url => url.kind === 'SD')?.url || '';
      const thumbnail = selectedContent.urls?.find(url => url.kind === 'THUMBNAIL')?.url || '';
      
      setHdPath(hd);
      setSdPath(sd);
      setThumbnailPath(thumbnail);
  
      // Log updates for debugging
      console.log("Title:", titleRef.current);
      console.log("Author:", artworkAuthorRef.current);
      console.log("Media paths set:", { hd, sd, thumbnail });
  
      // Initialize checkbox values
      contentDataRef.current.streaming = selectedContent.streaming ?? true;
      contentDataRef.current.splittable = selectedContent.splittable ?? true;
      contentDataRef.current.croppable = selectedContent.croppable ?? true;
      contentDataRef.current.deconstructable = selectedContent.deconstructable ?? true;
      console.log("[AddContent] Checkbox values set:", contentDataRef.current);
    } else {
      console.log("[AddContent] No selectedContent, resetting form.");
  
      // Reset refs to default values
      setContentId('');
      titleRef.current = '';
      artworkAuthorRef.current = '';
      setDescriptions([]);
      datationTextRef.current = '';
      datationHiddenRef.current = false;
      datationKindRef.current = false;
      categoriesRef.current = [];
      keywordsRef.current = [];
      languageRef.current = '';
      setHdPath('');
      setSdPath('');
      setThumbnailPath('');
      contentDataRef.current.streaming = true;
      contentDataRef.current.splittable = true;
      contentDataRef.current.croppable = true;
      contentDataRef.current.deconstructable = true;
    }
  }, [selectedContent]);  // The useEffect depends on selectedContent


  // 1.2 Descriptions and Datation

  // When descriptions are updated
  const handleDescriptionChange = (newDescriptions) => {
    setDescriptions(newDescriptions);  // Update the state for Descriptions component
    console.log("[AddContent] Updated descriptions:", descriptions);
  };

  // 3. Media

  // Function to handle file selection and update state
  const handleFileSelect = (event, versionType) => {
    const file = event.target.files[0];

    if (file) {
      console.log("[AddContent] File selected:", file, "for type:", versionType);

      // Update the selectedFiles state with the file object and name
      setSelectedFiles((prevState) => ({
        ...prevState,
        [`${versionType}FileName`]: file.name,  // Store the file name for display
        [`${versionType}File`]: file,  // Store the actual file object for upload
      }));

      // Dynamically update mediaRef for the correct version type
      mediaRef.current[`${versionType}Path`] = URL.createObjectURL(file);
    }
  };

  const handleCheckboxChange = (name, value) => {
    contentDataRef.current[name] = value;  // Update the correct field in the ref
    setTriggerRender(prev => !prev);  // Force a re-render to reflect changes in the UI
  };

  useEffect(() => {
    console.log("[AddContent] Current selected files:", selectedFiles);
  }, [selectedFiles]);

  const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: '4px 12px', // Adjust padding to control height
    backgroundColor: theme.palette.background.paper,
    marginLeft: 0,
    minHeight: 56, // Set the minimum height to 32px
    height: 56, // Ensure height is exactly 32px
    '&.Mui-expanded': {
      minHeight: 56, // Maintain height when expanded
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      color: theme.palette.text.primary, // Use text.primary color for the text
      alignItems: 'center', // Ensure content aligns properly within the smaller height
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: theme.palette.text.primary, // Use text.primary color for the expand icon
    },
  }));

  const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper, // Set background color
    paddingTop: theme.spacing(2), // Ensure padding is consistent
    marginLeft: 0, // Remove any unintended left margin
    marginRight: 0, // Remove any unintended right margin
  }));

  const CustomAccordion = styled(Accordion)({
    boxShadow: 'none',
    marginLeft: '0',
    border: 'none',
    backgroundColor: 'transparent', // Set the accordion background to transparent to let child elements handle the background
    width: '100%', // Ensure the accordion takes the full width
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setIsAccordionExpanded(isExpanded ? panel : false);
  };

  // 4. Rights

  const handleAddRight = () => {
    if (currentRight && currentRightCountry) {
      // Append the new right to the existing rights array
      setRights([...rights, { type: currentRight, country: currentRightCountry }]);

      // Reset the currentRight and currentRightCountry to their default values
      setCurrentRight('No access');
      setCurrentRightCountry(Literals.ALL);
    }
  };

  // 5. Credits

  const handleAddCredit = () => {
    if (creditName && creditType) {
      setCredits([...credits, { name: creditName, type: creditType, location: creditLocation }]);
      setCreditName('');
      setCreditType('');
      setCreditLocation('ALL');
    }
  };

  // Checkboxes uses for datation and media 
  const toggleRender = () => {
    setTriggerRender(!triggerRender); // Forces re-render
  };

  // S. Save
  // TODO Rainy Day: Refactor later handleSubmitForm if larger or more complex. 
  // It can be beneficial to break it down into smaller, more focused functions.

  const handleSubmitForm = async () => {
    setLoading(true);
    try {
      // Ensure all data is updated correctly
      handleDescriptionChange();
      handleAddRight();
      handleAddCredit();
      const datationKind = datationKindRef.current;
      const datationHidden = datationHiddenRef.current;

      const { datationStart, datationEnd } = convertDatationText(datationTextRef.current);
      const artworkCategories = categoriesRef.current.map(category => category.id);  // Extract only the IDs

      // Ensure descriptions are an array or use an empty array if undefined
      const validDescriptions = Array.isArray(descriptions) ? descriptions : [];

      // Prepare the contentData with descriptions, rights, and other fields
      const updatedContentData = {
        title: titleRef.current,
        artworkAuthor: artworkAuthorRef.current,
        artworkDescription: validDescriptions.map(desc => desc.description).join(', '),
        datationText: datationTextRef.current,
        datationStart,
        datationEnd,
        datationHidden,
        datationKind,
        categories: artworkCategories,
        keywords: keywordsRef.current, // Use this to pass the keywords array
        language: languageRef.current,
        media: mediaRef.current,  // Ensure the media paths are passed correctly
        descriptions,
        rights: rights,  // Include rights
        credits: credits, // Include credits
        streaming: contentDataRef.current.streaming,  // Make sure streaming is passed
        splittable: contentDataRef.current.splittable,
        croppable: contentDataRef.current.croppable,
        deconstructable: contentDataRef.current.deconstructable,
      };

      await handleUpload(updatedContentData, contentId, selectedFiles);
    } catch (error) {
      console.error("[AddContent] handleSubmitForm - Error submitting the form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <LoadingSpinner loading={loading} />  {/* Add spinner */}
      <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
        {contentId ? t("button.modify.content") : t("button.add.content")}
      </Typography>
      {/* 1. Main details */}
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>{t("_Main_")}</Typography>
      <Box component="form" sx={{ width: '100%' }}>

        {/* 1.1. Title and Author */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Change from row to column at the 'md' breakpoint
          gap: 2,
          mb: 3,
          flexWrap: 'wrap' // Allow wrapping when space is limited
        }}>
          <TextField
            label={t("_Title_")}
            defaultValue={titleRef.current}
            onChange={(e) => (titleRef.current = e.target.value)}
            sx={{ minWidth: '300px', flex: '1 1 auto' }} // Set minimum width and allow it to grow
          />
          <TextField
            label={t("ArtistName_")}
            defaultValue={artworkAuthorRef.current}
            onChange={(e) => (artworkAuthorRef.current = e.target.value)}
            sx={{ minWidth: '300px', flex: '1 1 auto' }} // Set minimum width and allow it to grow
          />
        </Box>

        {/* 1.2. Descriptions and Datation */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Change from row to column at the 'md' breakpoint
          gap: 2,
          mb: 3,
          flexWrap: 'wrap', // Allow wrapping when space is limited
          minWidth: { md: '300px' }, // Minimum width threshold for row layout
        }}>
          {/* 1.2.1. Descriptions */}
          <Box sx={{ flex: '1', minWidth: '300px' }}>
            <Descriptions
              descriptions={descriptions}
              setDescriptions={handleDescriptionChange}  // Update using setDescriptions
              currentDescription={currentDescription}
              setCurrentDescription={setCurrentDescription}
              currentDescriptionLang={currentDescriptionLang}
              setCurrentDescriptionLang={setCurrentDescriptionLang}
              currentDescriptionName={currentDescriptionName}
              setCurrentDescriptionName={setCurrentDescriptionName}
            />
          </Box>
          {/* 1.2.2. Datation */}
          <Box sx={{
            flex: '1',
            minWidth: '300px',
            mt: { md: 8, xs: 0 }, // Apply margin-top only when the screen width is above md breakpoint
          }}>
            <TextField
              label={t("_Datation_")}
              defaultValue={datationTextRef.current}
              onChange={(e) => (datationTextRef.current = e.target.value)}
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* <Box sx={{ flex: 1, minWidth: '50%' }}> */}
              <CustomCheckbox
                checked={datationHiddenRef.current}
                onChange={(name, checked) => {
                  console.log("[AddContent] Checkbox Changed:", name, checked); // Log changes
                  datationHiddenRef.current = checked;
                  contentDataRef.current.datationHidden = checked;
                  handleCheckboxChange(name, checked); // Handle the checkbox change globally
                }}
                name="datationHidden"
                label={t("DatationHidden_")}
              />
              <CustomCheckbox
                checked={datationKindRef.current}
                onChange={(name, checked) => {
                  console.log("[AddContent] Checkbox Changed:", name, checked); // Log changes
                  datationKindRef.current = checked;
                  contentDataRef.current.datationKind = checked;
                  handleCheckboxChange(name, checked); // Handle the checkbox change globally
                }}
                name="datationKind"
                label={t("Approximate_")}
              />
            </Box>
          </Box>
        </Box>
        {/* 2. Classification */}
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>{t("Classification_")}</Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              alignItems: 'stretch', // Ensures items stretch to the same height
              flexWrap: 'wrap', // Allows items to wrap if necessary
            }}
          >
            {/* 2.1. Categories Component */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px', // Full width on mobile, minWidth on larger screens
                mb: isMobile ? 2 : 0, // Adds margin on bottom when in column mode
              }}
            >
              <Categories
                setCategories={(newCategories) => {
                  setCategories(newCategories);  // Update the state with new categories
                  categoriesRef.current = newCategories;  // Update the ref to reflect the current state
                  console.log("[AddContent] Updated categories:", categoriesRef.current);
                }}
                checkedCategories={categoriesRef.current}
              />
            </Box>

            {/* 2.2. KeywordInput Component */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px', // Full width on mobile, minWidth on larger screens
              }}
            >
              <KeywordInput
                keywordsRef={keywordsRef}
                setKeywords={(newKeywords) => {
                  keywordsRef.current = newKeywords;
                  console.log("[AddContent] Updated keywords:", keywordsRef.current);
                }}
              />
            </Box>
          </Box>
        </Box>
        {/* 3. Media */}
        <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>{t("Media_")}</Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              alignItems: 'stretch', // Ensures items stretch to the same height
              flexWrap: 'wrap', // Allows items to wrap if necessary
            }}
          >
            {/* Media Left part */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px',
                mt: 1,
                mb: isMobile ? 2 : 0, // Adds margin on bottom when in column mode
              }}
            >
              <input
                accept="video/*, image/png, image/gif, image/jpeg, image/jpg, image/tiff"
                style={{ display: 'none' }}
                id="hd-upload"
                type="file"
                onChange={(e) => handleFileSelect(e, 'hd')}
              />
              <label htmlFor="hd-upload">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: theme.palette.divider, // Use theme divider color for the border
                    borderRadius: '4px', // Slightly round the corners
                    backgroundColor: theme.palette.background.paper, // Set background color to match other contained elements
                    p: 2, // Add padding to make it look more contained
                  }}
                >
                  <Typography variant="body2">
                    {t("BROWSE_HD_4K_")}
                  </Typography>
                  <FileUploadIcon
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.primary.main, // Apply the theme color
                      '&:hover': {
                        color: theme.palette.primary.dark, // Darken color on hover
                      }
                    }}
                  />
                </Box>
              </label>
              {selectedFiles.hdFile && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {t("Selected_file_")} {selectedFiles.hdFile.name}
                </Typography>
              )}
            </Box>

            {/* Right part */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px', // Full width on mobile, minWidth on larger screens
              }}
            >
              <Box sx={{ flex: 1, marginTop: 1, minWidth: isMobile ? '100%' : '300px' }}>
                <CustomAccordion expanded={isAccordionExpanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
                  <CustomAccordionSummary expandIcon={<ExpandMoreIcon />} >
                    <Typography variant="body2">
                      {t("BROWSE_SD_TN_")}
                    </Typography>
                  </CustomAccordionSummary>
                  <CustomAccordionDetails>
                    <input
                      accept="video/*, image/png, image/gif, image/jpeg, image/jpg, image/tiff"
                      style={{ display: 'none' }}
                      id="sd-upload"
                      type="file"
                      onChange={(e) => handleFileSelect(e, 'sd')}
                    />
                    <label htmlFor="sd-upload">
                      <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                        {t("BROWSE_SD_")}
                      </Button>
                    </label>
                    {selectedFiles.sdFile && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t("Selected_file_")}{selectedFiles.sdFileName}
                      </Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                    {/* Thumbnail Upload */}
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="thumbnail-upload"
                      type="file"
                      onChange={(e) => handleFileSelect(e, 'thumbnail')}
                    />
                    <label htmlFor="thumbnail-upload">
                      <Button variant="outlined" component="span" fullWidth>
                        {t("BROWSE_TN_")}
                      </Button>
                    </label>
                    {selectedFiles.thumbnailFile && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t("Selected_file_")}{selectedFiles.thumbnailFileName}
                      </Typography>
                    )}
                  </CustomAccordionDetails>
                </CustomAccordion>
                {/* Accordion for URLs */}
                <CustomAccordion expanded={isAccordionExpanded === 'panel2'} onChange={handleAccordionChange('panel2')} sx={{ mt: 2 }}>
                  <CustomAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t("URLs_")}</Typography>
                  </CustomAccordionSummary>
                  <AccordionDetails>
                    <TextField
                      fullWidth
                      label={t("HD_4K_Version_URL")}
                      value={hdPath}
                      onChange={(e) => setHdPath(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label={t("SD_Version_URL")}
                      value={sdPath}
                      onChange={(e) => setSdPath(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label={t("TN_Version_URL")}
                      value={thumbnailPath}
                      onChange={(e) => setThumbnailPath(e.target.value)}
                    />
                  </AccordionDetails>
                </CustomAccordion>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row', // Switches between row and column based on screen width
            gap: 2,
            alignItems: 'flex-start', // Align to the top
            flexWrap: 'wrap'
          }}
        >

          {/* Left Column for HD/4K Upload */}

          <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>

          </Box>

          {/* Right Column with Accordion for SD and Thumbnail Uploads */}

        </Box>

        {/* Thumbnail Colors Section */}
        {/* <Box>
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2">Thumbnail Colors</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel control={<Checkbox />} label="Color1" />
              <FormControlLabel control={<Checkbox />} label="Color2" />
              <FormControlLabel control={<Checkbox />} label="Color3" />
              <Button variant="outlined">Use a color picker</Button>
            </Box>
          </Box>
        </Box> */}


        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              alignItems: 'stretch', // Ensures items stretch to the same height
              flexWrap: 'wrap', // Allows items to wrap if necessary
            }}
          >
            {/*  Left part  */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px', // Full width on mobile, minWidth on larger screens
                mb: isMobile ? 2 : 0, // Adds margin on bottom when in column mode
              }}
            >
            </Box>
            {/* Right part */}
            <Box
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '300px', // Full width on mobile, minWidth on larger screens
              }}
            >
            </Box>
          </Box>
        </Box>
        {/* 4. Rights */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }, // Column on mobile, row on larger screens
            gap: 2, // Add gap between elements
            alignItems: 'stretch', // Ensures items stretch to the same height
            flexWrap: 'wrap', // Allows items to wrap if necessary
          }}
        >
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Rights
              rights={rights}
              setRights={setRights}
              currentRight={currentRight}
              setCurrentRight={setCurrentRight}
              currentRightCountry={currentRightCountry}
              setCurrentRightCountry={setCurrentRightCountry}
              selectVariant="outlined"
              rightsDirection="row"
              formControlHeight="56px"
            />
          </Box>
        </Box>
        <Grid container spacing={2} sx={{ justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
          <Grid item xs={6} sm={3} display="flex" justifyContent="flex-start">
            <CustomCheckbox
              checked={contentDataRef.current.streaming} // Access streaming from useRef
              onChange={(name, checked) => handleCheckboxChange(name, checked)}  // Correct handler call
              name="streaming"
              label={t("Streaming_")}
            />
          </Grid>
          <Grid item xs={6} sm={3} display="flex" justifyContent="flex-start">
            <CustomCheckbox
              checked={contentDataRef.current.splittable}
              onChange={(name, checked) => handleCheckboxChange(name, checked)}  // Correct handler call
              name="splittable"
              label={t("Splittable_")}
            />
          </Grid>
          <Grid item xs={6} sm={3} display="flex" justifyContent="flex-start">
            <CustomCheckbox
              checked={contentDataRef.current.croppable}
              onChange={(name, checked) => handleCheckboxChange(name, checked)}  // Correct handler call
              name="croppable"
              label={t("Croppable_")}
            />
          </Grid>
          <Grid item xs={6} sm={3} display="flex" justifyContent="flex-start">
            <CustomCheckbox
              checked={contentDataRef.current.deconstructable}
              onChange={(name, checked) => handleCheckboxChange(name, checked)}  // Correct handler call
              name="deconstructable"
              label={t("Deconstructable_")}
            />
          </Grid>
        </Grid>
        {/* 5. Credits */}
        <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>{t("Credits_")}</Typography>
        <Credits
          credits={credits}
          setCredits={setCredits}
          creditName={creditName}
          setCreditName={setCreditName}
          creditType={creditType}
          setCreditType={setCreditType}
          creditLocation={creditLocation}
          setCreditLocation={setCreditLocation}
          handleAddCredit={handleAddCredit}
        />
        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitForm}
            disabled={loading}
          >
            {contentId ? t("Save_Changes_") : t("Add_Content_")}
          </Button>
        </Box>
      </Box>
    </div>
  );
}
export default AddContent;